import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import NewsCard from "@/components/base/NewsCard";
import SectionTitle from "@/components/base/SectionTitle";

export default function CategoryPage() {
  const { type = "film" } = useParams();
  const category = categories.find((c) => c.key === type) || categories[0];

  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [activeTag, setActiveTag] = useState("全部");
  const [visible, setVisible] = useState(6);

  const baseList = useMemo(() => news.filter((n) => n.category === category.key), [category.key]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((n) => n.tags.forEach((t) => set.add(t)));
    return ["全部", ...Array.from(set)];
  }, [baseList]);

  const filtered = useMemo(() => {
    let list = activeTag === "全部" ? baseList : baseList.filter((n) => n.tags.includes(activeTag));
    list = [...list].sort((a, b) => (sort === "hot" ? b.views - a.views : b.date.localeCompare(a.date)));
    return list;
  }, [baseList, activeTag, sort]);

  const shown = filtered.slice(0, visible);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>资讯</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{category.name}</span>
      </nav>

      <div className="mb-8 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <SectionTitle eyebrow="CHANNEL" title={category.name} desc={category.description} />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-full border border-background-200 px-1 py-1">
            {[
              { key: "latest", label: "最新" },
              { key: "hot", label: "最热" },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  setSort(s.key as "latest" | "hot");
                  setVisible(6);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  sort === s.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setActiveTag(t);
                  setVisible(6);
                }}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  activeTag === t
                    ? "border-accent-400 bg-accent-500/15 text-accent-300"
                    : "border-background-200 text-foreground-600 hover:border-accent-400 hover:text-accent-400"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          {visible < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + 6)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                加载更多 <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}
          {visible >= filtered.length && filtered.length > 0 && (
            <p className="mt-8 text-center text-xs text-foreground-500">已加载全部内容</p>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16">
          <i className="ri-inbox-line text-4xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">该筛选条件下暂无内容，试试其他标签。</p>
        </div>
      )}
    </div>
  );
}